import { Cell, ChatCell, ChatMessage, ConversationCell } from '@/explore/types';
import { generateUUID } from '@/lib/utils';

import { generateCellId, belongsToConversation } from '.';

export const isChatCell = (cell: Cell): cell is ChatCell => cell.kind === 'chat';

export const buildConversationCell = (): ConversationCell => {
  return {
    id: generateCellId(),
    kind: 'conversation',
    conversationId: generateUUID(),
  };
};

export const buildEmptyChatCell = (conversationId: string): ChatCell => {
  return {
    id: generateCellId(),
    kind: 'chat',
    conversationId,
    messages: [],
  };
};

export const updateChatCellMessage = (cell: ChatCell, updatedMessage: ChatMessage): ChatCell =>
  updateChatCellMessages(
    cell,
    cell.messages.map((message) => (message.id === updatedMessage.id ? updatedMessage : message)),
  );

export const updateChatCellMessages = (cell: ChatCell, messages: ChatMessage[]): ChatCell => ({
  ...cell,
  messages,
});

export const isLastChatCell = (cells: Cell[], cell: Cell): boolean =>
  cells.findLast(isChatCell)?.id === cell.id;

/**
 * Removes all conversation cells after the last chat cell in the list.
 * @param cells
 * @param conversationId
 * @returns
 */
export const trimTrailingNotChatCells = (cells: Cell[], conversationId: string): Cell[] => {
  const lastChatCellIndex = cells.findLastIndex(
    (c) => isChatCell(c) && belongsToConversation(c, conversationId),
  );
  return cells.filter((c, idx) => {
    if (!belongsToConversation(c, conversationId)) {
      return true;
    }
    return idx <= lastChatCellIndex;
  });
};

export const getLastUserMessageIndex = (messages: ChatCell['messages']) =>
  messages.findLastIndex((msg) => msg.role === 'user');

export const getLastInputCellIndex = (
  cells: Cell[],
  conversationId: string,
  cellsToIgnore: string[] = [],
) => {
  return cells.findLastIndex(
    (cell) =>
      !cellsToIgnore.includes(cell.id) &&
      belongsToConversation(cell, conversationId) &&
      isChatCell(cell) &&
      cell.messages.some((message) => message.role === 'user'),
  );
};

export const removeLastChatInput = (cells: Cell[], conversationId: string, cell: ChatCell) => {
  const lastInputCellIndex = getLastInputCellIndex(
    cells,
    conversationId,
    cell.messages.length < 2 ? [cell.id] : [],
  );

  return cells
    .map((c, index) => {
      if (index === lastInputCellIndex && isChatCell(c)) {
        const cellMessages = cell.id === c.id ? cell.messages : c.messages;
        const lastMessageIndex = getLastUserMessageIndex(cellMessages);
        return {
          ...c,
          messages: cellMessages.slice(0, lastMessageIndex === 0 ? 1 : lastMessageIndex),
        };
      }

      return c;
    })
    .filter((c, index) => {
      if (!belongsToConversation(c, cell.conversationId)) {
        return true;
      }

      return index <= lastInputCellIndex;
    });
};
